import React, { useEffect, type FC } from "react";

import { useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";

import { useRecoilCallback, useRecoilValue } from "recoil";

import { evalCondition } from "mailup-react-helpers";

import {
	BUTTON_VARIANT_PRIMARY,
	Button,
	ContextMenu,
	Flex,
	HelpIcon,
	IconArrow1Down,
	LINK_VARIANT_DEFAULT,
	Link,
	TEXT_VARIANT_BOLD,
	TITLE_VARIANT_XL,
	Text,
	Title,
	TooltipUtils,
	makeButtonWithMenu,
} from "mailup-web-components";

import { isNullOrEmpty } from "Helpers/strings";

//  import { panelEditRecipientCloseCallback } from 'Pages/RecipientsList/recoil/callbacks/recipient'
import { modalSendConfirmationOpenCallback } from "Pages/RecipientsList/recoil/callbacks/modalSendConfirmation";
import {
	modalDeleteOpenCallback,
	modalUnsubscribeOpenCallback,
} from "Pages/RecipientsList/recoil/callbacks/modals";
import { panelEditRecipientState } from "Pages/RecipientsList/recoil/state/recipient";

import IconChevronsRight from "Components/icons/IconChevronsRight";

import { panelEditContactsOpenCallback } from "Pages/RecipientsList/recoil/callbacks/editContacts";
import { modalSubscribeOpenCallback } from "Pages/RecipientsList/recoil/callbacks/modalSubscribe";
import { panelEditRecipientCloseCallback } from "Pages/RecipientsList/recoil/callbacks/recipient";

import RenderWhen from "Components/RenderWhen";
import EngagementUpsellingView from "Pages/RecipientsList/components/results/EngagementUpsellingView";
import EngagementRating from "Pages/RecipientsList/components/results/EngagementView";
import { gainsightTrack } from "mailup-react-helpers";

const ActionsButtonComponent: FC = () => {
	const intl = useIntl();

	return (
		<Button kind={BUTTON_VARIANT_PRIMARY}>
			<Flex alignItems="center" gap="8px">
				<span>{intl.formatMessage({ id: "Load.MailupCommon.Actions" })}</span>
				<IconArrow1Down />
			</Flex>
		</Button>
	);
};

const ActionsMenuComponent: FC = () => {
	const intl = useIntl();
	const navigate = useNavigate();

	const { recipient } = useRecoilValue(panelEditRecipientState);

	const panelEditContacts = useRecoilCallback(
		panelEditContactsOpenCallback,
		[],
	);

	const modalUnsubscribeOpen = useRecoilCallback(
		modalUnsubscribeOpenCallback,
		[],
	);

	const modalDeleteOpen = useRecoilCallback(modalDeleteOpenCallback, []);

	const handleSendConfirmationEmail = useRecoilCallback(
		modalSendConfirmationOpenCallback,
		[],
	);

	const handleSubscribeOpen = useRecoilCallback(modalSubscribeOpenCallback, []);

	const hasAdvRecMgmt: boolean = evalCondition("co:ADVRECIPIENTMGMT");

	const handleActionEditContacts = (): void => {
		if (recipient !== undefined) {
			void panelEditContacts(recipient.Id);
		}
	};

	const handleActionUnsubscribe = (_id: number): void => {
		if (recipient !== undefined) {
			gainsightTrack(
				"mup_recipients_click_selectedRecipientUnsubscribeActionMenu",{});
			void modalUnsubscribeOpen(recipient.Id);
		}
	};

	const handleActionStatistics = (_id: number): void => {
		if (recipient !== undefined) {
			navigate(`/recipients/profile/${recipient.Id}/trend`);
		}
	};

	const handleActionDelete = (_id: number): void => {
		gainsightTrack("mup_highlight_click_deleteRecipientsTC", {});
		if (recipient !== undefined) {
			void modalDeleteOpen(recipient.Id);
		}
	};

	const items = [
		{
			label: intl.formatMessage({
				id: "Load.RecipientsList.ActionEditContacts",
			}),
			"data-mu-test": "action-edit-contacts",
			onClick: handleActionEditContacts,
		},
		recipient?.actions.sendConfirmationEmail === true
			? {
					label: intl.formatMessage({
						id: "Load.RecipientsList.ActionSendConfirmationEmail",
					}),
					"data-mu-test": "action-send-confirmation-email",
					onClick: handleSendConfirmationEmail,
				}
			: undefined,

		hasAdvRecMgmt
			? {
					label: intl.formatMessage({
						id: "Load.RecipientsList.ActionSubscribeMultilist",
					}),
					"data-mu-test": "action-subscribe-multilist",
					onClick: handleSubscribeOpen,
				}
			: undefined,
		{
			label: intl.formatMessage({
				id: "Load.RecipientsList.ActionViewStatistics",
			}),
			"data-mu-test": "action-statistics",
			onClick: handleActionStatistics,
		},
		{
			label: intl.formatMessage({
				id: "Load.RecipientsList.ActionUnsubscribe",
			}),
			"data-mu-test": "action-unsubscribe",
			onClick: handleActionUnsubscribe,
		},
		{
			label: intl.formatMessage({
				id: "Load.RecipientsList.ActionDelete",
			}),
			"data-mu-test": "action-delete",
			onClick: handleActionDelete,
			separator: true,
		},
	].filter((o) => o !== undefined);

	return <ContextMenu items={items} direction="down" />;
};

const Actions = makeButtonWithMenu(
	ActionsButtonComponent,
	ActionsMenuComponent,
);

const PanelEditRecipientHeader: FC = () => {
	const intl = useIntl();
	const { recipient, score, title } = useRecoilValue(panelEditRecipientState);

	const handleClose = useRecoilCallback(panelEditRecipientCloseCallback, []);

	useEffect(() => {
		setTimeout(TooltipUtils.rebuild as () => void, 500);
	}, []);

	const canEdit: boolean = evalCondition("uo:EDIT");
	const hasDelStats: boolean = evalCondition("co:DELSTATS OR co:ENGAGEMENT");

	return (
		<Flex flexDirection="column" gap="16px">
			<Flex alignItems="center" gap="16px">
				<Link
					variant={LINK_VARIANT_DEFAULT}
					onClick={handleClose}
					data-mu-test="close-side-panel"
					data-tip={intl.formatMessage({
						id: "Load.RecipientsList.PanelCloseTooltip",
					})}
				>
					<IconChevronsRight width="14px" height="14px" />
				</Link>
			</Flex>

			{recipient !== undefined && (
				<Flex flexDirection="column" gap="8px">
					<Flex
						alignItems="flex-start"
						justifyContent="space-between"
						gap="32px"
					>
						<Title variant={TITLE_VARIANT_XL} overflowWrap="anywhere">
							{isNullOrEmpty(title) ? (
								<em>
									{intl.formatMessage({ id: "Load.RecipientsList.EmptyData" })}
								</em>
							) : (
								title
							)}
						</Title>

						{canEdit && <Actions menuAlign="right" direction="down" />}
					</Flex>

					<Flex alignItems="center" gap="16px">
						<Text variant={TEXT_VARIANT_BOLD}>
							{intl.formatMessage({
								id: "Load.RecipientsList.ColumnEngagement",
							})}
						</Text>

						{score !== undefined && (
							<Flex alignItems="center" gap="4px">
								<RenderWhen condition={hasDelStats}>
									<EngagementRating score={score} />
								</RenderWhen>
								<RenderWhen condition={!hasDelStats}>
									<EngagementUpsellingView />
								</RenderWhen>

								<HelpIcon
									data-tip
									data-for="engagement-tooltip"
									inline={false}
								/>
							</Flex>
						)}
					</Flex>
				</Flex>
			)}
		</Flex>
	);
};
export default PanelEditRecipientHeader;
